import { Components, Theme, keyframes, styled } from '@mui/material/styles';

// Checkbox box uses a custom span icon (not the MUI SVG) so we can apply the
// inset shadow + brand fill from theme tokens. Size is driven by the `--cb-size`
// custom property set on the root (medium 22px, small 19px).
const CheckboxBox = styled('span')(({ theme }) => ({
  boxSizing: 'border-box',
  position: 'relative',
  width: 'var(--cb-size, 22px)',
  height: 'var(--cb-size, 22px)',
  borderRadius: 4,
  backgroundColor: theme.palette.common.white,
  border: `1px solid ${theme.palette.customColors.checkboxBorder}`,
  // Root gets `.Mui-disabled`; the icon span is its descendant. Use a descendant
  // selector instead of `input:disabled ~ &` (sibling match was unreliable).
  '.Mui-disabled &': {
    backgroundColor: theme.palette.grey[200], // Gray when disabled
    borderColor: theme.palette.grey[200], // Light Gray
  },
}));

const CheckboxBoxChecked = styled(CheckboxBox)(({ theme }) => ({
  backgroundColor: theme.palette.customColors.checkboxChecked,
  border: 'none',
  boxShadow: '0px 1px 3px 0px #00000080 inset',
  // CSS checkmark, white on the brand fill.
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '46%',
    left: '50%',
    width: 5,
    height: 9,
    border: `solid ${theme.palette.common.white}`,
    borderWidth: '0 2px 2px 0',
    transform: 'translate(-50%, -50%) rotate(45deg)',
  },
  '.Mui-disabled &': {
    backgroundColor: theme.palette.grey[500], // Gray when disabled
    borderColor: theme.palette.grey[500],
  },
}));

export const MuiCheckbox: Components<Theme>['MuiCheckbox'] = {
  defaultProps: {
    icon: <CheckboxBox />,
    checkedIcon: <CheckboxBoxChecked />,
  },
  styleOverrides: {
    root: ({ ownerState }) => ({
      // padding: 0,
      ...(ownerState.size === 'small' && {
        '--cb-size': '19px',
      }),
    }),
  },
};

export const MuiFormControlLabel: Components<Theme>['MuiFormControlLabel'] = {
  styleOverrides: {
    // Negative margin pulls the label flush with the surrounding layout,
    // compensating for the control's internal padding. Checkbox padding is a
    // touch larger than radio's, so it needs a slightly bigger offset.
    root: {
      '&:has(.MuiRadio-root)': {
        marginLeft: '0',
        gap: '10px',
      },
      '&:has(.MuiCheckbox-root)': {
        marginLeft: '-8px',
        gap: '1px',
      },
    },
  },
};

// Default gap for FormGroups (radio & checkbox groups). Set via defaultProps.sx
// rather than styleOverrides on purpose: a selector-scoped styleOverrides rule
// (e.g. `&[role="radiogroup"]`) is always anchored to the root class by MUI, so
// it ties a consumer's own sx at specificity (0,1,0) AND is emitted afterwards —
// meaning the theme would win the tie and clobber sx={{ gap }} at usage sites.
// defaultProps.sx instead shallow-merges into the instance sx ({...default,
// ...instance}), so an instance's own gap simply wins by object precedence.
export const MuiFormGroup: Components<Theme>['MuiFormGroup'] = {
  defaultProps: {
    sx: { gap: '10px' },
  },
};

// Radio uses a custom span icon (not the MUI SVG) so the control is exactly
// 20x20 with no surrounding box — MUI's default SvgIcon renders the glyph inside
// a larger font-size em box, which reads as padding around the circle.
// Inner dot scales in on check, mirroring MUI's default radio transition
// (its dot animates transform on a single persistent SVG node; our checked icon
// mounts fresh, so a keyframe reproduces the same pop-in).
const dotScaleIn = keyframes`
  from { transform: translate(-50%, -50%) scale(0); }
  to { transform: translate(-50%, -50%) scale(1); }
`;

const RadioCircle = styled('span')(({ theme }) => ({
  boxSizing: 'border-box',
  width: 20,
  height: 20,
  borderRadius: '50%',
  backgroundColor: theme.palette.common.white,
  border: `1px solid ${theme.palette.customColors.checkboxBorder}`,
  transition: theme.transitions.create(['border-color', 'background-color'], {
    duration: theme.transitions.duration.shortest,
  }),
  '.Mui-disabled &': {
    borderColor: theme.palette.grey[200],
  },
}));

const RadioCircleChecked = styled(RadioCircle)(({ theme }) => ({
  position: 'relative',
  // Inner filled dot (r=6 in the source SVG => 12px diameter).
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: 12,
    height: 12,
    borderRadius: '50%',
    backgroundColor: theme.palette.customColors.checkboxChecked,
    transform: 'translate(-50%, -50%)',
    animation: `${dotScaleIn} ${theme.transitions.duration.shortest}ms ${theme.transitions.easing.easeInOut}`,
  },
  '.Mui-disabled &::after': {
    backgroundColor: theme.palette.grey[500],
  },
}));

export const MuiRadio: Components<Theme>['MuiRadio'] = {
  defaultProps: {
    icon: <RadioCircle />,
    checkedIcon: <RadioCircleChecked />,
  },
  styleOverrides: {
    root: {
      padding: 0,
    },
  },
};
